import { useGetAgendaByIdQuery } from 'store/services/flow/agenda';
import { useGetUsableViewQuery } from 'store/services/flow/views';
import { memo, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TABLE_CELL_TYPE, VIEW_ITEM_STATE } from 'types/enums';
import { Table } from 'components/common/Table/Table';

import { getCellTypeByFormType, getFormItemType, transformDataForTable } from 'utils/agendaRecord/agendaRecord';
import { getSelectRowId } from 'utils/table.ts';
import { useTableQueryControls } from 'utils/hooks';
import { SearchField } from 'components/common/Table/actions';
import { styled } from '@mui/material';
import type { AgendaRecordItem, DataSchema, GetAgendaRecordsRes, Schema, UserAgendaTableDataSchemaType } from 'types';

const TableHeaderWrapper = styled('div')(({ theme }) => ({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'flex-end',
    margin: 0,
    gap: '5px',
  },
}));

interface RecordsFilterTableProps {
  agendaId: string;
  viewId: string;
  selectedRows: string[];
  onSelectRow: (rowIds: string[]) => void;
  data: GetAgendaRecordsRes<AgendaRecordItem[]>;
  isLoading?: boolean;
  autoFocusSearch?: boolean;
  onStateChange: (params: { page: number; pageSize: number; search: string; orderBy: string; orderDesc: boolean; formItemId: string }) => void;
}

export const RecordsFilterTable = memo(
  ({ agendaId, viewId, selectedRows, onSelectRow, data, isLoading, autoFocusSearch = false, onStateChange }: RecordsFilterTableProps) => {
    const { t } = useTranslation();

    const { data: agendaData } = useGetAgendaByIdQuery({
      id: agendaId,
    });

    const isHeliosTable = useMemo(() => !!agendaData?.module?.isHeliosVisible, [agendaData]);

    const { data: { viewParameters = [] } = {} } = useGetUsableViewQuery(
      {
        parentId: agendaId,
        viewId: viewId,
        viewParentType: 'Agenda',
      },
      { skip: !viewId }
    );

    const { state, setPagination, setSort, setSearch } = useTableQueryControls<typeof dataSchema, Record<string, string>>(
      `recordsFilter-${agendaId}-${viewId}`,
      {
        filters: {},
        sort: { by: '', order: 'asc', type: '' },
        search: '',
        pagination: { page: 1, pageSize: 10 },
      }
    );

    const dataSchema: UserAgendaTableDataSchemaType = useMemo(() => {
      return viewParameters
        .filter(({ viewItemState }) => [VIEW_ITEM_STATE.ENABLED_AND_FILTER, VIEW_ITEM_STATE.ENABLED_AND_NO_FILTER].includes(viewItemState))
        .reduce<UserAgendaTableDataSchemaType>((acc, item) => {
          acc[item.parameterId ?? ''] = {
            label: item.parameterId,
            formItemDefinition: item.formItemDefinition,
            type: getCellTypeByFormType(item.formItemType!) as TABLE_CELL_TYPE,
            originalType: String(item.formItemType),
            customKey: String(item.parameterTitle),
            group: '',
            groupName: '',
            groupOrder: 0,
            dateMask: item.formItemDefinition?.date?.dateMask || '',
            isEditable: false,
          };
          return acc;
        }, {});
    }, [viewParameters]);

    const notifyStateChange = useCallback(() => {
      onStateChange({
        page: state?.pagination.page ?? 1,
        pageSize: state?.pagination.pageSize ?? 10,
        search: state?.search ?? '',
        orderBy: state?.sort?.type ?? '',
        orderDesc: state?.sort?.order === 'desc',
        formItemId: state?.sort?.by ?? '',
      });
    }, [state, onStateChange]);

    useEffect(() => {
      notifyStateChange();
    }, [notifyStateChange]);

    const transformedData = useMemo(() => transformDataForTable(data.items || []), [data.items]);

    const handleSelectRows = useCallback(
      (_rowIds: string[], rows: Schema<string | number, DataSchema>[] = []) => {
        onSelectRow(rows.map((row) => getSelectRowId(row, isHeliosTable)));
      },
      [isHeliosTable, onSelectRow]
    );

    const getTableKeys = useCallback(
      (forSort?: boolean) => {
        const types = [TABLE_CELL_TYPE.TEXT, TABLE_CELL_TYPE.USERNAME, TABLE_CELL_TYPE.EMAIL, TABLE_CELL_TYPE.DATE];
        return Object.keys(dataSchema).filter((key) => {
          const includesType = types.includes(dataSchema[key].type);
          return forSort ? !includesType : includesType;
        });
      },
      [dataSchema]
    );

    return (
      <Table
        key={`table-${agendaId}-${viewId}-${selectedRows.length}-${selectedRows.join(',')}`}
        isHeliosTable={isHeliosTable}
        data={transformedData as Schema<string, DataSchema>[]}
        dataSchema={dataSchema}
        hideCheckbox={false}
        isMultiSelect={true}
        defaultSelectedRows={selectedRows}
        onSelectRow={handleSelectRows}
        hidePagination={false}
        hideHeader={false}
        isLoading={isLoading}
        rows={data.totalCount || 0}
        page={data.page}
        pageSize={data.pageSize ?? 10}
        unableSortFor={getTableKeys(true)}
        tableId={`records-filter-${agendaId}-${viewId}`}
        paginationZIndex={12003}
        onPageSizeChange={(pageSize) => {
          setPagination({
            page: 1,
            pageSize,
          });
        }}
        onCurrentPageChange={(page) => {
          setPagination({
            page,
            pageSize: data?.pageSize ?? 10,
          });
        }}
        onSort={(props) => {
          const { column, direction } = props;
          const type = getFormItemType(data, column as string);
          setSort({
            by: column as string,
            order: direction,
            type,
          });
        }}
        sort={{
          column: state?.sort?.by ?? 'id',
          direction: state?.sort?.order ?? 'asc',
        }}
        controls={
          <TableHeaderWrapper>
            <SearchField
              placeholder={t('common.search.records')}
              value={state?.search || ''}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus={autoFocusSearch}
            />
          </TableHeaderWrapper>
        }
      />
    );
  }
);
